import { useCallback, useEffect, useState } from "react"

import { AudioVideoFacade } from "amazon-chime-sdk-js"

import { Subscription } from "@rails/actioncable"

import EventEmitter from "src/EventEmitter"

export type useRemoteAVControlsProps = {
  audioVideo: AudioVideoFacade
  channel: Subscription
  localUser: {
    type: "Student" | "Tutor" | "Teacher" | "Admin"
    id: string
  }
  session: any
  muteLocalAudio: VoidFunction
  muteLocalVideo: VoidFunction
  unmuteLocalAudio: VoidFunction
  unmuteLocalVideo: VoidFunction
}

const useRemoteAVControls = ({
  audioVideo,
  channel,
  session,
  localUser,
  muteLocalAudio,
  muteLocalVideo,
  unmuteLocalAudio,
  unmuteLocalVideo,
}: useRemoteAVControlsProps) => {
  // SET UP STATE

  const [localControlsLocked, setLocalControlsLocked] = useState<boolean>(
    session.controlsLockedUserIds.includes(localUser.id)
  )

  const [allAudioMuted, setAllAudioMuted] = useState<boolean>(false)
  const [allVideoMuted, setAllVideoMuted] = useState<boolean>(false)
  const [allControlsLocked, setAllControlsLocked] = useState<boolean>(false)
  const [controlsLockedUserIds, setControlsLockedUserIds] = useState<string[]>(
    session.controlsLockedUserIds.map(id => id)
  )

  useEffect(() => {
    if (!audioVideo || !channel || !localUser.type) return

    const isTutor = localUser.type === "Tutor"
    const isStudent = localUser.type === "Student"

    const onMuteAll = data => {
      if (isTutor) {
        if (data.type === "audio") setAllAudioMuted(true)
        if (data.type === "video") setAllVideoMuted(true)
        setAllControlsLocked(true)
        setControlsLockedUserIds(data.controlsLockedUserIds)
      } else if (isStudent) {
        if (data.type === "audio") muteLocalAudio()
        if (data.type === "video") muteLocalVideo()
        setLocalControlsLocked(true)
      }
    }
    EventEmitter.subscribe("mute_all", onMuteAll)

    const onUnmuteAll = data => {
      if (isTutor) {
        if (data.type === "audio") setAllAudioMuted(false)
        if (data.type === "video") setAllVideoMuted(false)
        setAllControlsLocked(false)
        setControlsLockedUserIds(data.controlsLockedUserIds)
      } else if (isStudent) {
        if (data.type === "audio") unmuteLocalAudio()
        if (data.type === "video") unmuteLocalVideo()
        setLocalControlsLocked(false)
      }
    }
    EventEmitter.subscribe("unmute_all", onUnmuteAll)

    const onMuteUser = data => {
      if (isStudent && data.userId === localUser.id) {
        if (data.type === "audio") muteLocalAudio()
        if (data.type === "video") muteLocalVideo()
      }
    }
    EventEmitter.subscribe("mute_user", onMuteUser)

    const onUnmuteUser = data => {
      if (isTutor) {
        if (data.type === "audio") setAllAudioMuted(false)
        if (data.type === "video") setAllVideoMuted(false)
      } else if (isStudent && data.userId === localUser.id) {
        if (data.type === "audio") unmuteLocalAudio()
        if (data.type === "video") unmuteLocalVideo()
      }
    }
    EventEmitter.subscribe("unmute_user", onUnmuteUser)

    const onLockAll = data => {
      if (isTutor) {
        setAllControlsLocked(true)
        setControlsLockedUserIds(data.controlsLockedUserIds)
      } else if (isStudent) {
        setLocalControlsLocked(true)
      }
    }
    EventEmitter.subscribe("lock_all", onLockAll)

    const onUnlockAll = data => {
      if (isTutor) {
        setAllControlsLocked(false)
        setControlsLockedUserIds(data.controlsLockedUserIds)
      } else if (isStudent) {
        setLocalControlsLocked(false)
      }
    }
    EventEmitter.subscribe("unlock_all", onUnlockAll)

    const onLockUser = data => {
      if (isTutor) {
        setControlsLockedUserIds(data.controlsLockedUserIds)
      } else if (isStudent && data.userId === localUser.id) {
        setLocalControlsLocked(true)
      }
    }
    EventEmitter.subscribe("lock_user", onLockUser)

    const onUnlockUser = data => {
      if (isTutor) {
        setAllControlsLocked(false)
        setControlsLockedUserIds(data.controlsLockedUserIds)
      } else if (isStudent && data.userId === localUser.id) {
        setLocalControlsLocked(false)
      }
    }
    EventEmitter.subscribe("unlock_user", onUnlockUser)

    return () => {
      EventEmitter.unsubscribe("mute_all", onMuteAll)
      EventEmitter.unsubscribe("unmute_all", onUnmuteAll)
      EventEmitter.unsubscribe("mute_user", onMuteUser)
      EventEmitter.unsubscribe("unmute_user", onUnmuteUser)
      EventEmitter.unsubscribe("lock_all", onLockAll)
      EventEmitter.unsubscribe("unlock_all", onUnlockAll)
      EventEmitter.unsubscribe("lock_user", onLockUser)
      EventEmitter.unsubscribe("unlock_user", onUnlockUser)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audioVideo, channel, localUser.type])

  const toggleAllAudio = useCallback(() => {
    if (allAudioMuted) {
      channel.perform("unmute_all", { type: "audio" })
    } else {
      channel.perform("mute_all", { type: "audio" })
    }
  }, [channel, allAudioMuted])

  const toggleAllVideo = useCallback(() => {
    if (allVideoMuted) {
      channel.perform("unmute_all", { type: "video" })
    } else {
      channel.perform("mute_all", { type: "video" })
    }
  }, [channel, allVideoMuted])

  const toggleAllControlsLocked = useCallback(() => {
    if (allControlsLocked) {
      channel.perform("unlock_all")
    } else {
      channel.perform("lock_all")
    }
  }, [channel, allControlsLocked])

  return {
    localControlsLocked,
    allAudioMuted,
    toggleAllAudio,
    allVideoMuted,
    toggleAllVideo,
    allControlsLocked,
    toggleAllControlsLocked,
    controlsLockedUserIds,
  }
}

export default useRemoteAVControls
